import {
  type CmsRow,
  type RateScheduleDraft as Draft,
  type MMRate,
  type RateScheduleProductLine as ProductLine,
  type RateBand,
  US_STATE_CODES,
  type YearRate,
  bandRow,
  buildRateScheduleConfig as buildConfig,
  cmsRow,
  draftFromConfigRow as draftFromRow,
  emptyRateScheduleDraft as emptyDraft,
  mmRow,
  summarizeRateSchedule as summarize,
  yrRow,
} from '@commissionsight/shared';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { Button, Card, Field, inputClass } from './ui.js';

interface ScheduleRow {
  id: string;
  workspaceId: string | null;
  carrierId: string | null;
  productLine: ProductLine;
  config: Record<string, unknown>;
  isActive: boolean;
}

const LINE_LABEL: Record<ProductLine, string> = {
  major_medical: 'Major medical',
  medicare: 'Medicare (MA / MAPD / PDP)',
  ancillary: 'Ancillary',
};

/**
 * Customer-facing editor for product-line commission rate schedules (Medicare CMS
 * tables, ancillary premium schedules, major-medical percent/flat), scoped by
 * workspace + carrier. Structured forms build a config that the API validates
 * against its product-line shape. Direct fetch (kept off the SDK, like the other
 * UI-only surfaces). The pure draft↔config logic lives in @commissionsight/shared
 * (`rate-schedule-form`) and is unit-tested there.
 */
export function RateScheduleEditor({ carriers }: { carriers: { id: string; name: string }[] }) {
  const { baseUrl, token, workspacesEnabled, workspaces } = useAuth();
  const [rows, setRows] = useState<ScheduleRow[] | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editId, setEditId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const api = useCallback(
    (path: string, init?: RequestInit) =>
      fetch(`${baseUrl.replace(/\/$/, '')}/rate-schedules${path}`, {
        ...init,
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      }),
    [baseUrl, token],
  );

  const load = useCallback(() => {
    api('')
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => res && setRows(res.schedules))
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const carrierName = (id: string | null) =>
    id ? (carriers.find((c) => c.id === id)?.name ?? id.slice(0, 8)) : 'All carriers';
  const wsName = (id: string | null) =>
    id ? (workspaces.find((w) => w.id === id)?.name ?? id.slice(0, 8)) : 'All workspaces';

  const startAdd = () => {
    setDraft(emptyDraft());
    setEditId(null);
    setError(null);
    setOpen(true);
  };
  const startEdit = (row: ScheduleRow) => {
    setDraft(draftFromRow(row));
    setEditId(row.id);
    setError(null);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const config = buildConfig(draft);
      const res = editId
        ? await api(`/${editId}`, { method: 'PUT', body: JSON.stringify({ config }) })
        : await api('', {
            method: 'POST',
            body: JSON.stringify({
              workspaceId: draft.workspaceId || null,
              carrierId: draft.carrierId || null,
              config,
            }),
          });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.title ?? `Save failed (${res.status})`);
      }
      setOpen(false);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (s: ScheduleRow) => {
    await api(`/${s.id}`, { method: 'PUT', body: JSON.stringify({ isActive: !s.isActive }) });
    load();
  };
  const remove = async (s: ScheduleRow) => {
    await api(`/${s.id}`, { method: 'DELETE' });
    load();
  };

  return (
    <Card>
      <div className="mb-1 flex items-center justify-between">
        <h2 className="font-semibold">Product-line commission rules</h2>
        {!open && (
          <Button variant="ghost" onClick={startAdd}>
            + Add schedule
          </Button>
        )}
      </div>
      <p className="mb-4 text-sm text-neutral-500">
        How your contracted commission is computed per product line, workspace, and carrier —
        Medicare CMS tables, ancillary premium schedules, or a percent/flat major-medical rate.
        Changes apply to the next statement you process.
      </p>

      {/* Existing schedules */}
      {rows && rows.length > 0 && (
        <div className="mb-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500">
                <th className="py-1.5">Product line</th>
                <th className="py-1.5">Workspace</th>
                <th className="py-1.5">Carrier</th>
                <th className="py-1.5">Schedule</th>
                <th className="py-1.5">Status</th>
                <th className="py-1.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-t border-neutral-100 dark:border-neutral-800">
                  <td className="py-2 font-medium">{LINE_LABEL[s.productLine]}</td>
                  <td className="py-2 text-neutral-500">
                    {workspacesEnabled ? wsName(s.workspaceId) : '—'}
                  </td>
                  <td className="py-2 text-neutral-500">{carrierName(s.carrierId)}</td>
                  <td className="py-2 text-neutral-600 dark:text-neutral-300">{summarize(s)}</td>
                  <td className="py-2">
                    <button
                      type="button"
                      onClick={() => toggle(s)}
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${s.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'}`}
                    >
                      {s.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => startEdit(s)}
                      className="mr-3 text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(s)}
                      className="text-xs font-medium text-rose-600 hover:underline dark:text-rose-400"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="rounded-lg border border-neutral-200 p-4 dark:border-neutral-800">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">{editId ? 'Edit schedule' : 'New schedule'}</h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-neutral-500 hover:text-neutral-800"
            >
              Cancel
            </button>
          </div>

          {/* Scope */}
          <div className="mb-4 grid gap-3 sm:grid-cols-3">
            <Field label="Product line">
              <select
                className={inputClass}
                value={draft.line}
                disabled={!!editId}
                onChange={(e) => setDraft((d) => ({ ...d, line: e.target.value as ProductLine }))}
              >
                <option value="major_medical">Major medical</option>
                <option value="medicare">Medicare</option>
                <option value="ancillary">Ancillary</option>
              </select>
            </Field>
            {workspacesEnabled && (
              <Field label="Workspace">
                <select
                  className={inputClass}
                  value={draft.workspaceId}
                  disabled={!!editId}
                  onChange={(e) => setDraft((d) => ({ ...d, workspaceId: e.target.value }))}
                >
                  <option value="">All workspaces</option>
                  {workspaces.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Carrier">
              <select
                className={inputClass}
                value={draft.carrierId}
                disabled={!!editId}
                onChange={(e) => setDraft((d) => ({ ...d, carrierId: e.target.value }))}
              >
                <option value="">All carriers</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {draft.line === 'major_medical' && <MajorMedicalForm draft={draft} setDraft={setDraft} />}
          {draft.line === 'medicare' && <MedicareForm draft={draft} setDraft={setDraft} />}
          {draft.line === 'ancillary' && <AncillaryForm draft={draft} setDraft={setDraft} />}

          {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
          <div className="mt-4 flex gap-2">
            <Button onClick={save} loading={busy}>
              {editId ? 'Save changes' : 'Add schedule'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

type SetDraft = Dispatch<SetStateAction<Draft>>;
const cell = `${inputClass} py-1`;
const smallBtn =
  'rounded-md border border-neutral-300 px-2 py-1 text-xs text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700';

function RowActions({ onAdd, onRemove }: { onAdd: () => void; onRemove?: () => void }) {
  return (
    <div className="flex gap-2">
      {onRemove && (
        <button type="button" className={smallBtn} onClick={onRemove}>
          Remove
        </button>
      )}
      <button type="button" className={smallBtn} onClick={onAdd}>
        + Add row
      </button>
    </div>
  );
}

function MajorMedicalForm({ draft, setDraft }: { draft: Draft; setDraft: SetDraft }) {
  const set = (i: number, patch: Partial<MMRate>) =>
    setDraft((d) => ({ ...d, mm: d.mm.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const overridePercent =
    draft.mmOverrideMethod === 'percent_of_premium' ||
    draft.mmOverrideMethod === 'percent_of_commission';
  return (
    <div className="space-y-3">
      <div className="text-xs font-medium text-neutral-500">
        Rate tiers (blank plan = carrier default). Add a premium band or a first-year/renewal scope
        to grade the rate; the most specific matching tier wins.
      </div>
      {draft.mm.map((r, i) => (
        <div
          key={r.uid}
          className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800"
        >
          <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
            <Field label="Plan code">
              <input
                className={cell}
                value={r.planCode}
                placeholder="all plans"
                onChange={(e) => set(i, { planCode: e.target.value })}
              />
            </Field>
            <Field label="Basis">
              <select
                className={cell}
                value={r.rateType}
                onChange={(e) => set(i, { rateType: e.target.value as MMRate['rateType'] })}
              >
                <option value="percent_of_premium">% of premium</option>
                <option value="flat_per_member">Flat / PMPM per member</option>
              </select>
            </Field>
            <Field label={r.rateType === 'percent_of_premium' ? 'Percent' : 'Dollars'}>
              <input
                className={cell}
                type="number"
                min="0"
                step="any"
                value={r.rateValue}
                onChange={(e) => set(i, { rateValue: e.target.value })}
              />
            </Field>
            <RowActions
              onAdd={() => setDraft((d) => ({ ...d, mm: [...d.mm, mmRow()] }))}
              onRemove={
                draft.mm.length > 1
                  ? () => setDraft((d) => ({ ...d, mm: d.mm.filter((_, j) => j !== i) }))
                  : undefined
              }
            />
          </div>
          <div className="mt-2 grid items-end gap-2 sm:grid-cols-3">
            <Field label="Min premium (band, optional)">
              <input
                className={cell}
                type="number"
                min="0"
                step="any"
                value={r.minPremium}
                placeholder="—"
                onChange={(e) => set(i, { minPremium: e.target.value })}
              />
            </Field>
            <Field label="Max premium (band, optional)">
              <input
                className={cell}
                type="number"
                min="0"
                step="any"
                value={r.maxPremium}
                placeholder="—"
                onChange={(e) => set(i, { maxPremium: e.target.value })}
              />
            </Field>
            <Field label="Applies to">
              <select
                className={cell}
                value={r.compensationType}
                onChange={(e) =>
                  set(i, { compensationType: e.target.value as MMRate['compensationType'] })
                }
              >
                <option value="">First-year &amp; renewal</option>
                <option value="initial">First-year only</option>
                <option value="renewal">Renewal only</option>
              </select>
            </Field>
          </div>
        </div>
      ))}
      <MMOverrideFields
        method={draft.mmOverrideMethod}
        rate={draft.mmOverrideRate}
        isPercent={overridePercent}
        onMethod={(m) => setDraft((d) => ({ ...d, mmOverrideMethod: m }))}
        onRate={(v) => setDraft((d) => ({ ...d, mmOverrideRate: v }))}
      />
    </div>
  );
}

/** Optional production-bonus override for a graded major-medical schedule. */
function MMOverrideFields({
  method,
  rate,
  isPercent,
  onMethod,
  onRate,
}: {
  method: Draft['mmOverrideMethod'];
  rate: string;
  isPercent: boolean;
  onMethod: (m: Draft['mmOverrideMethod']) => void;
  onRate: (v: string) => void;
}) {
  return (
    <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <div className="text-xs font-medium text-neutral-500">Production bonus (optional)</div>
      <p className="mb-2 text-[11px] text-neutral-400">
        A bonus added on top of the base tiers — a bonus you were owed but not paid shows up as
        owed.
      </p>
      <div className="grid items-end gap-2 sm:grid-cols-2">
        <Field label="Bonus type">
          <select
            className={cell}
            value={method}
            onChange={(e) => onMethod(e.target.value as Draft['mmOverrideMethod'])}
          >
            <option value="">None</option>
            <option value="percent_of_premium">% of premium</option>
            <option value="percent_of_commission">% of commission</option>
            <option value="flat_per_member">Flat per member</option>
            <option value="per_member_per_month">Per member / month (PMPM)</option>
          </select>
        </Field>
        {method && (
          <Field label={isPercent ? 'Bonus percent' : 'Bonus dollars'}>
            <input
              className={cell}
              type="number"
              min="0"
              step="any"
              value={rate}
              onChange={(e) => onRate(e.target.value)}
            />
          </Field>
        )}
      </div>
    </div>
  );
}

function MedicareForm({ draft, setDraft }: { draft: Draft; setDraft: SetDraft }) {
  const set = (i: number, patch: Partial<CmsRow>) =>
    setDraft((d) => ({ ...d, cms: d.cms.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-neutral-500">CMS max-compensation table</div>
      <p className="text-[11px] text-neutral-400">
        Region: leave blank for the national (US) rate, or enter a 2-letter state code for a
        state-specific CMS cap (e.g. CA, NJ, CT, PA, PR, VI). Each member is matched to their state
        from the statement, falling back to the national rate.
      </p>
      {/* Shared typeahead list for every region input below. */}
      <datalist id="cms-region-codes">
        {US_STATE_CODES.map((code) => (
          <option key={code} value={code} />
        ))}
      </datalist>
      {draft.cms.map((r, i) => (
        <div
          key={r.uid}
          className="grid items-end gap-2 sm:grid-cols-[0.8fr_0.8fr_0.8fr_1fr_0.9fr_auto]"
        >
          <Field label="Year">
            <input
              className={cell}
              type="number"
              value={r.contractYear}
              onChange={(e) => set(i, { contractYear: e.target.value })}
            />
          </Field>
          <Field label="Product">
            <select
              className={cell}
              value={r.productType}
              onChange={(e) => set(i, { productType: e.target.value as CmsRow['productType'] })}
            >
              <option value="MA">MA</option>
              <option value="MAPD">MAPD</option>
              <option value="PDP">PDP</option>
            </select>
          </Field>
          <Field label="Region">
            <input
              className={cell}
              value={r.region}
              list="cms-region-codes"
              placeholder="US"
              maxLength={2}
              onChange={(e) => set(i, { region: e.target.value.toUpperCase() })}
            />
          </Field>
          <Field label="Comp type">
            <select
              className={cell}
              value={r.compensationType}
              onChange={(e) =>
                set(i, { compensationType: e.target.value as CmsRow['compensationType'] })
              }
            >
              <option value="initial">initial</option>
              <option value="renewal">renewal</option>
            </select>
          </Field>
          <Field label="Annual $">
            <input
              className={cell}
              type="number"
              min="0"
              step="any"
              value={r.annualAmount}
              onChange={(e) => set(i, { annualAmount: e.target.value })}
            />
          </Field>
          <RowActions
            onAdd={() =>
              setDraft((d) => ({
                ...d,
                cms: [...d.cms, cmsRow()],
              }))
            }
            onRemove={
              draft.cms.length > 1
                ? () => setDraft((d) => ({ ...d, cms: d.cms.filter((_, j) => j !== i) }))
                : undefined
            }
          />
        </div>
      ))}
      <div className="w-40">
        <Field label="RDE months (rapid disenroll)">
          <input
            className={cell}
            type="number"
            min="1"
            max="12"
            value={draft.rdeMonths}
            onChange={(e) => setDraft((d) => ({ ...d, rdeMonths: e.target.value }))}
          />
        </Field>
      </div>
      <div className="w-64">
        <Field label="Base payment cadence">
          <select
            className={cell}
            value={draft.medicareCadence}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                medicareCadence: e.target.value as Draft['medicareCadence'],
              }))
            }
          >
            <option value="annual_upfront">Annual upfront (lump)</option>
            <option value="monthly">Monthly installments</option>
          </select>
        </Field>
        <p className="mt-1 text-xs text-neutral-500">
          How the carrier pays the CMS base. Pick “Monthly” only if this carrier pays renewals in
          equal monthly installments — otherwise owed is overstated each period.
        </p>
      </div>
    </div>
  );
}

/** Editable policy-year band rows (from/to/rate), shared by flat + dimensioned. */
function YearRateRows({
  rows,
  onChange,
}: {
  rows: YearRate[];
  onChange: (rows: YearRate[]) => void;
}) {
  const set = (i: number, patch: Partial<YearRate>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={r.uid} className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <Field label="From year">
            <input
              className={cell}
              type="number"
              min="1"
              value={r.fromYear}
              onChange={(e) => set(i, { fromYear: e.target.value })}
            />
          </Field>
          <Field label="To year (blank = open)">
            <input
              className={cell}
              type="number"
              min="1"
              value={r.toYear}
              placeholder="∞"
              onChange={(e) => set(i, { toYear: e.target.value })}
            />
          </Field>
          <Field label="Rate %">
            <input
              className={cell}
              type="number"
              min="0"
              step="any"
              value={r.rate}
              onChange={(e) => set(i, { rate: e.target.value })}
            />
          </Field>
          <RowActions
            onAdd={() => onChange([...rows, yrRow()])}
            onRemove={rows.length > 1 ? () => onChange(rows.filter((_, j) => j !== i)) : undefined}
          />
        </div>
      ))}
    </div>
  );
}

function AncillaryForm({ draft, setDraft }: { draft: Draft; setDraft: SetDraft }) {
  const anyAge = draft.bands.some((b) => b.ageBand !== '');
  const setBand = (i: number, patch: Partial<RateBand>) =>
    setDraft((d) => ({ ...d, bands: d.bands.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Product family">
          <input
            className={cell}
            value={draft.family}
            placeholder="med_supp"
            onChange={(e) => setDraft((d) => ({ ...d, family: e.target.value }))}
          />
        </Field>
        <Field label="Method">
          <select
            className={cell}
            value={draft.method}
            onChange={(e) => setDraft((d) => ({ ...d, method: e.target.value as Draft['method'] }))}
          >
            <option value="as_earned">As earned</option>
            <option value="advanced">Advanced</option>
          </select>
        </Field>
        {draft.method === 'advanced' && (
          <Field label="Advance fraction (1 / 0.75 / 0.5)">
            <input
              className={cell}
              type="number"
              min="0"
              max="1"
              step="any"
              value={draft.advanceFraction}
              onChange={(e) => setDraft((d) => ({ ...d, advanceFraction: e.target.value }))}
            />
          </Field>
        )}
      </div>

      <datalist id="anc-state-codes">
        {US_STATE_CODES.map((code) => (
          <option key={code} value={code} />
        ))}
      </datalist>

      {/* Flat vs dimensioned mode */}
      <Field label="Rate structure">
        <select
          className={cell}
          value={draft.ancMode}
          onChange={(e) => setDraft((d) => ({ ...d, ancMode: e.target.value as Draft['ancMode'] }))}
        >
          <option value="flat">Flat — one rate set for all states & ages</option>
          <option value="dimensioned">By state / issue-age / product (rate matrix)</option>
        </select>
      </Field>

      {draft.ancMode === 'flat' ? (
        <>
          <div className="text-xs font-medium text-neutral-500">Policy-year rate bands</div>
          <YearRateRows
            rows={draft.yearRates}
            onChange={(rows) => setDraft((d) => ({ ...d, yearRates: rows }))}
          />
        </>
      ) : (
        <div className="space-y-3">
          <p className="text-[11px] text-neutral-400">
            Each cell applies only to records matching its state, issue-age, and product. Leave a
            dimension blank for “any”. The most specific matching cell wins; rows that match no cell
            are left unevaluated (not mis-scored).
          </p>
          {draft.bands.map((b, i) => (
            <div
              key={b.uid}
              className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800"
            >
              <div className="mb-2 grid items-end gap-2 sm:grid-cols-[0.7fr_1fr_1.6fr_auto]">
                <Field label="State">
                  <input
                    className={cell}
                    value={b.state}
                    list="anc-state-codes"
                    placeholder="any"
                    maxLength={2}
                    onChange={(e) => setBand(i, { state: e.target.value.toUpperCase() })}
                  />
                </Field>
                <Field label="Issue age">
                  <select
                    className={cell}
                    value={b.ageBand}
                    onChange={(e) => setBand(i, { ageBand: e.target.value as RateBand['ageBand'] })}
                  >
                    <option value="">Any age</option>
                    <option value="under_65">Under 65</option>
                    <option value="65_plus">65+</option>
                  </select>
                </Field>
                <Field label="Product match (plan-name keywords/regex)">
                  <input
                    className={cell}
                    value={b.planPattern}
                    placeholder="any product — e.g. medicare supplement|med supp"
                    onChange={(e) => setBand(i, { planPattern: e.target.value })}
                  />
                </Field>
                <div className="pb-0.5">
                  <button
                    type="button"
                    className={smallBtn}
                    disabled={draft.bands.length <= 1}
                    onClick={() =>
                      setDraft((d) => ({ ...d, bands: d.bands.filter((_, j) => j !== i) }))
                    }
                  >
                    Remove cell
                  </button>
                </div>
              </div>
              <YearRateRows
                rows={b.yearRates}
                onChange={(rows) => setBand(i, { yearRates: rows })}
              />
            </div>
          ))}
          <button
            type="button"
            className={smallBtn}
            onClick={() => setDraft((d) => ({ ...d, bands: [...d.bands, bandRow()] }))}
          >
            + Add rate cell
          </button>
          {anyAge && (
            <Field label="Under-65 classifier — plan-name regex (matched = under 65)">
              <input
                className={cell}
                value={draft.under65Pattern}
                placeholder="\bDIS\b|DISABILITY"
                onChange={(e) => setDraft((d) => ({ ...d, under65Pattern: e.target.value }))}
              />
            </Field>
          )}
          {anyAge && (
            <p className="text-[11px] text-neutral-400">
              How a row is classified under-65 vs 65+. Member date-of-birth (age at effective date)
              is used when present; otherwise this plan-name pattern decides — e.g. disability
              Med-Supp plans carrying “DIS”.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
